import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Megaphone, UsersThree, PaperPlaneTilt, Trash, Archive } from '@phosphor-icons/react';
import {
  PageHeader,
  Surface,
  Button,
  Input,
  Textarea,
  Label,
  Badge,
  Modal,
  EmptyState,
  Spinner,
  useToast,
} from '@/components/ui-kit';
import { api } from '@/lib/api';
import { useInstances } from '@/context/InstanceContext';
import { fmtDate } from '@/lib/utils';

export default function CampaignsListPage() {
  const { current, instances } = useInstances();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [pageError, setPageError] = useState('');
  const { show, node: toastNode } = useToast();

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const list = await api.listCampaigns();
      setCampaigns(list);
      setPageError('');
    } catch (e) {
      const message = `Erro: ${e?.response?.data?.detail || e.message}`;
      setPageError(message);
      show(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line
  }, []);

  const remove = async (id) => {
    if (!window.confirm('Excluir esta campanha? Os grupos no WhatsApp permanecem intactos — apenas o vínculo aqui é removido.')) return;
    try {
      await api.deleteCampaign(id);
      show('Campanha excluída', 'success');
      fetchCampaigns();
    } catch (e) {
      show(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
  };

  const archive = async (c) => {
    try {
      await api.updateCampaign(c.id, { status: c.status === 'archived' ? 'active' : 'archived' });
      show(c.status === 'archived' ? 'Campanha reativada' : 'Campanha arquivada', 'success');
      fetchCampaigns();
    } catch (e) {
      show(`Erro: ${e?.response?.data?.detail || e.message}`, 'error');
    }
  };

  return (
    <div data-testid="page-campaigns-list">
      <PageHeader
        badge="// Workspaces"
        title="Campanhas"
        subtitle="Cada campanha é um espaço de trabalho com seus próprios grupos, mensagens agendadas e histórico. Crie uma campanha para começar."
        actions={
          <Button onClick={() => setShowCreate(true)} disabled={instances.length === 0} data-testid="open-create-campaign-btn">
            <Plus size={14} weight="bold" />
            Nova campanha
          </Button>
        }
      />

      {pageError && (
        <Surface className="p-4 mb-5 border-red-500/30 bg-red-500/10" data-testid="campaigns-page-error">
          <p className="text-sm font-mono text-red-300">{pageError}</p>
          <p className="text-xs text-red-200/70 mt-1">
            Campanhas precisam de um MongoDB acessível para serem salvas.
          </p>
        </Surface>
      )}

      {loading ? (
        <div className="text-zinc-400 flex items-center gap-2"><Spinner /> carregando…</div>
      ) : campaigns.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="Nenhuma campanha criada"
          hint="Comece criando sua primeira campanha. A instância operacional é escolhida no cadastro e pode ser trocada depois."
          action={
            <Button onClick={() => setShowCreate(true)} disabled={instances.length === 0} data-testid="empty-create-campaign-btn">
              Criar campanha
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {campaigns.map((c) => (
            <CampaignCard
              key={c.id}
              c={c}
              instances={instances}
              onArchive={() => archive(c)}
              onDelete={() => remove(c.id)}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCampaignModal
          instance={current}
          instances={instances}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            fetchCampaigns();
            show('Campanha criada!', 'success');
          }}
          onError={(m) => show(m, 'error')}
        />
      )}
      {toastNode}
    </div>
  );
}

function CampaignCard({ c, instances, onArchive, onDelete }) {
  const isArchived = c.status === 'archived';
  const linkedInstance = instances.find((i) => i.instanceName === c.instanceName);
  const instanceMissing = c.instanceName && !linkedInstance;
  return (
    <Surface
      className="p-5 hover:border-zinc-700 transition-colors duration-200"
      data-testid={`campaign-card-${c.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <Link
            to={`/campaigns/${c.id}`}
            className="font-display font-bold text-lg text-zinc-50 hover:text-lime-400 transition-colors block truncate"
            data-testid={`open-campaign-${c.id}`}
          >
            {c.name}
          </Link>
          {c.description && (
            <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{c.description}</p>
          )}
        </div>
        <Badge variant={isArchived ? 'default' : 'success'}>
          {isArchived ? 'arquivada' : 'ativa'}
        </Badge>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-mono">
        <div className="flex items-center gap-1.5 text-zinc-400">
          <UsersThree size={12} className="text-lime-500" />
          <span className="text-zinc-200">{c.groupsCount || 0}</span> grupos
        </div>
        <div className="flex items-center gap-1.5 text-zinc-400">
          <PaperPlaneTilt size={12} className="text-lime-500" />
          <span className="text-zinc-200">{c.dispatchesCount || 0}</span> disparos
        </div>
      </div>

      <div className="mt-3 text-xs font-mono">
        <span className="text-zinc-500">instância: </span>
        <span className={instanceMissing ? 'text-amber-300' : 'text-zinc-300'}>
          {c.instanceName || 'não definida'}
        </span>
        {instanceMissing && <span className="text-zinc-500"> · removida/desconectada</span>}
      </div>

      <div className="mt-4 pt-3 border-t border-zinc-700 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.12em] text-zinc-500">
        <span>{fmtDate(c.createdAt)}</span>
        <div className="flex gap-1">
          <button
            onClick={onArchive}
            className="text-zinc-400 hover:text-amber-400 transition-colors p-1"
            title={isArchived ? 'Reativar' : 'Arquivar'}
            data-testid={`archive-${c.id}`}
          >
            <Archive size={14} />
          </button>
          <button
            onClick={onDelete}
            className="text-zinc-400 hover:text-red-400 transition-colors p-1"
            title="Excluir"
            data-testid={`delete-campaign-${c.id}`}
          >
            <Trash size={14} />
          </button>
        </div>
      </div>
    </Surface>
  );
}

function CreateCampaignModal({ instance, instances, onClose, onDone, onError }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instanceName, setInstanceName] = useState(
    instance || instances.find((i) => i.connectionStatus === 'open')?.instanceName || instances[0]?.instanceName || ''
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!name.trim()) return;
    if (!instanceName) {
      const message = 'Escolha uma instância para comandar a campanha';
      setError(message);
      onError(message);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.createCampaign({ instanceName, name: name.trim(), description: description.trim() || undefined });
      onDone();
    } catch (e) {
      const message = `Erro: ${e?.response?.data?.detail || e.message}`;
      setError(message);
      onError(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Nova campanha"
      testId="create-campaign-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy || !name.trim()} data-testid="confirm-create-campaign">
            {busy ? <Spinner /> : 'Criar campanha'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-mono text-red-300" data-testid="create-campaign-error">
            {error}
          </div>
        )}
        <div>
          <Label>Instância operacional</Label>
          <select
            value={instanceName}
            onChange={(e) => setInstanceName(e.target.value)}
            className="w-full bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm px-3 py-2 rounded-sm font-mono"
            data-testid="campaign-instance-select"
          >
            <option value="">Selecione uma instância</option>
            {instances.map((inst) => (
              <option key={inst.instanceName} value={inst.instanceName}>
                {inst.instanceName} - {inst.connectionStatus || 'sem status'}
              </option>
            ))}
          </select>
          <p className="text-xs text-zinc-400 mt-2 font-mono">
            Essa instância executa as ações da campanha. Você pode trocar depois dentro da campanha.
          </p>
        </div>
        <div>
          <Label>Nome da campanha</Label>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex: Lançamento Janeiro"
            data-testid="campaign-name-input"
          />
        </div>
        <div>
          <Label>Descrição (opcional)</Label>
          <Textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="contexto, objetivo, anotações…"
            data-testid="campaign-description-input"
          />
        </div>
      </div>
    </Modal>
  );
}
